import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Alert, Typography, Box, Chip, CircularProgress,
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Texting the people entered in a league or a tournament.
//
// Two steps on purpose. The first asks the server who would get this, what the
// message will actually say once the club name and opt-out line are added, and
// what it will cost; only then does the Send button appear. Nothing is sent
// from the message box alone, because a text cannot be recalled.
//
// The recipient list is resolved server-side — this dialog never sends numbers,
// only the event it is looking at.
const AUDIENCES = [
  { value: 'all', label: 'Everyone entered' },
  { value: 'unpaid', label: 'Not paid yet' },
  { value: 'waitlisted', label: 'Wait-listed only' },
];

export default function EventSmsDialog({ open, onClose, kind, eventId, eventName }) {
  const [audience, setAudience] = useState('all');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setAudience('all'); setMessage(''); setPreview(null);
    setResult(null); setError(''); setBusy(false);
  };
  const close = () => { reset(); onClose(); };

  const call = async (dryRun) => {
    setBusy(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'sendEventSms');
      const { data } = await fn({ kind, eventId, audience, message: message.trim(), dryRun });
      if (dryRun) setPreview(data); else { setResult(data); setPreview(null); }
    } catch (e) {
      setError(e.message || 'That did not work.');
    }
    setBusy(false);
  };

  // Any edit invalidates the preview: the figures on screen must always belong
  // to the message that would go out, not to an earlier draft of it.
  const edit = (setter) => (v) => { setter(v); setPreview(null); };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Text entrants — {eventName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {result ? (
          <Box>
            <Alert severity={result.failed ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Sent to {result.sent} of {result.recipients}
              {result.failed ? `, ${result.failed} failed` : ''}.
            </Alert>
            {result.failures?.length > 0 && (
              <Box sx={{ mb: 1 }}>
                {result.failures.map(f => (
                  <Typography key={f.to} variant="caption" sx={{ display: 'block', color: '#888' }}>
                    {f.name || f.to}: {f.message}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              For schedule changes, rain calls and court assignments — the things someone
              gave you a number for. Anything promotional needs their consent first.
            </Alert>

            <TextField select fullWidth label="Who" value={audience} size="small"
              onChange={e => edit(setAudience)(e.target.value)} sx={{ mb: 2 }}>
              {AUDIENCES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </TextField>

            <TextField fullWidth multiline minRows={3} label="Message"
              value={message} onChange={e => edit(setMessage)(e.target.value)}
              placeholder="Rain delay — first serve moved to 10:30."
              helperText="The club name is added in front and an opt-out line after, so keep it short."
            />

            {preview && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                  What arrives
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{preview.body}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={`${preview.recipients} recipient(s)`} />
                  <Chip size="small" label={`${preview.segments} segment(s) each`} />
                  <Chip size="small" label={`about $${preview.estimatedCostUsd.toFixed(2)}`} />
                </Box>
                {(preview.skipped?.declined > 0 || preview.skipped?.optedOut > 0
                  || preview.skipped?.noNumber > 0 || preview.skipped?.duplicate > 0) && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 1 }}>
                    Not included:{' '}
                    {[
                      preview.skipped.declined && `${preview.skipped.declined} declined`,
                      preview.skipped.optedOut && `${preview.skipped.optedOut} opted out`,
                      preview.skipped.noNumber && `${preview.skipped.noNumber} with no usable number`,
                      preview.skipped.duplicate && `${preview.skipped.duplicate} sharing a number`,
                    ].filter(Boolean).join(', ')}.
                  </Typography>
                )}
                {preview.preview?.some(p => p.role === 'partner') && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#888', mt: 0.5 }}>
                    Includes doubles partners, whose numbers came from their partner's entry.
                  </Typography>
                )}
                {preview.recipients === 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#B26A00', mt: 1 }}>
                    Nobody to text with these settings.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && !preview && (
          <Button variant="outlined" disabled={busy || !message.trim()} onClick={() => call(true)}>
            {busy ? <CircularProgress size={18} /> : 'Check first'}
          </Button>
        )}
        {!result && preview && (
          <Button variant="contained" color="primary"
            disabled={busy || preview.recipients === 0} onClick={() => call(false)}>
            {busy ? <CircularProgress size={18} /> : `Send to ${preview.recipients}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
